// ============================================================================
// ORGANISATION FORM MODAL — thin EnterpriseFormModal shell driven by a definition
// ============================================================================

import React, { useCallback } from 'react';
import { EnterpriseFormModal, type FormModalMode } from 'components/enterprise-shell';
import { organisationService } from 'api/services/organisationService';
import type { OrganisationRow, OrganisationScreenDefinition } from './organisationScreen.types';

interface OrganisationFormModalProps<TRecord extends OrganisationRow, TForm extends object> {
  definition: OrganisationScreenDefinition<TRecord, TForm>;
  tenantId: string;
  isOpen: boolean;
  mode: FormModalMode;
  record: TRecord | null;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  onClose: () => void;
  onSaved: () => void;
  onRestoreModal: (mode: FormModalMode, instanceId?: string, recordCode?: string) => void;
}

export function OrganisationFormModal<TRecord extends OrganisationRow, TForm extends object>({
  definition, tenantId, isOpen, mode, record, tabId, moduleName, createInstanceId,
  onClose, onSaved, onRestoreModal,
}: OrganisationFormModalProps<TRecord, TForm>): React.ReactElement | null {
  const { kind, noun, emptyForm, populate, validate, toRequest, renderFields, recordCode, recordName, formSize } = definition;

  const handleSave = useCallback(async (form: TForm, m: FormModalMode, rec: TRecord | null) => {
    const body = toRequest(form);
    if (m === 'edit' && rec) {
      const res = await organisationService.update<TRecord>(kind, tenantId, rec.id, body);
      if (!res.success) throw new Error(res.message);
    } else {
      const res = await organisationService.create<TRecord>(kind, tenantId, body);
      if (!res.success) throw new Error(res.message);
    }
  }, [kind, tenantId, toRequest]);

  return (
    <EnterpriseFormModal<TForm, TRecord>
      isOpen={isOpen}
      mode={mode}
      record={record}
      noun={noun}
      emptyForm={emptyForm}
      populateOnOpen={populate}
      validate={validate}
      onSave={handleSave}
      size={formSize ?? 'md'}
      tabId={tabId}
      moduleName={moduleName || definition.moduleName}
      createInstanceId={createInstanceId}
      recordKey={record ? recordCode(record) : undefined}
      getStripRecordLabel={(rec) => (rec ? `${recordCode(rec)} - ${recordName(rec)}` : undefined)}
      onClose={onClose}
      onSaved={onSaved}
      onRestoreModal={onRestoreModal}
    >
      {(args) => renderFields({ ...args, tenantId })}
    </EnterpriseFormModal>
  );
}

export default OrganisationFormModal;
